<template>
  <div class="emp-home-page">
    <!-- 앱 헤더 -->
    <div class="app-header">
      <div class="header-title">홈</div>
    </div>

    <div class="page-content">
    <!-- 환영 메시지 -->
    <div class="welcome-section">
      <div class="welcome-text">
        <h2>안녕하세요, {{ userName }}님!</h2>
        <p>오늘도 좋은 하루 되세요 ✨</p>
      </div>
      <div class="date-info">
        <span class="today">{{ todayText }}</span>
      </div>
    </div>
    
    <!-- 빠른 메뉴 -->
    <div class="quick-menu-section">
      <h3 class="section-title">빠른 메뉴</h3>
      <div class="quick-menu-grid">
        <div 
          v-for="menu in quickMenus" 
          :key="menu.path"
          class="quick-menu-item"
          @click="navigateTo(menu.path)"
        >
          <div class="menu-icon" :style="{ backgroundColor: menu.bgColor }">
            <v-icon :color="menu.iconColor" size="28">{{ menu.icon }}</v-icon>
          </div>
          <span class="menu-label">{{ menu.label }}</span>
        </div>
      </div>
    </div>

    <!-- 최근 급여 정보 -->
    <div class="recent-wage-section" v-if="recentWage">
      <h3 class="section-title">
        <v-icon size="20" color="#059669">mdi-cash-check</v-icon>
        최근 급여
      </h3>
      <div class="wage-card" @click="navigateTo('/emp/payslip')">
        <div class="wage-header">
          <span class="wage-month">{{ recentWage.month }}월 급여</span>
          <span class="wage-status" :class="{ 'confirmed': recentWage.is_closed === 'y' }">
            {{ recentWage.is_closed === 'y' ? '확정' : '진행중' }}
          </span>
        </div>
        <div class="wage-amount">
          <span class="label">실수령액</span>
          <span class="amount">{{ formatCurrency(recentWage.ewi_net_pay) }}<span class="unit">원</span></span>
        </div>
        <div class="wage-detail">
          <div class="detail-item">
            <span class="detail-label">총 지급액</span>
            <span class="detail-value">{{ formatCurrency(recentWage.ewi_total_pay) }}원</span>
          </div>
          <div class="detail-item" v-if="parseInt(recentWage.ewi_emp_total_deduction || 0) > 0">
            <span class="detail-label">총 공제액</span>
            <span class="detail-value deduct">{{ formatCurrency(recentWage.ewi_emp_total_deduction) }}원</span>
          </div>
        </div>
        <div class="view-more">
          <span>상세보기</span>
          <v-icon size="16" color="#059669">mdi-chevron-right</v-icon>
        </div>
      </div>
    </div>

    <!-- 공지사항 목록 -->
    <div class="notice-section">
      <div class="section-header">
        <h3 class="section-title">
          <v-icon size="20" color="#059669">mdi-bullhorn</v-icon>
          공지사항
        </h3>
        <span class="more-link" @click="goToNotice">더보기</span>
      </div>
      <div class="notice-list-card">
        <div v-if="noticeList.length > 0">
          <div 
            v-for="notice in noticeList" 
            :key="notice.nc_idx" 
            class="notice-list-item"
            @click="goToNoticeDetail(notice.nc_idx)"
          >
            <span :class="['mini-badge', `badge-${notice.nc_category?.toLowerCase()}`]">
              {{ getCategoryText(notice.nc_category) }}
            </span>
            <span class="notice-title">{{ notice.nc_title }}</span>
            <span class="notice-date">{{ formatNoticeDate(notice.nc_wdate) }}</span>
          </div>
        </div>
        <div v-else class="empty-notice">
          <v-icon size="24" color="#ccc">mdi-bulletin-board</v-icon>
          <span>공지사항이 없습니다</span>
        </div>
      </div>
    </div>

    <!-- 내 정보 요약 -->
    <div class="info-summary-section">
      <h3 class="section-title">내 정보</h3>
      <div class="info-card">
        <div class="info-row">
          <span class="info-label">소속</span>
          <span class="info-value">{{ sisulName }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">직종</span>
          <span class="info-value">{{ userJob }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">입사일</span>
          <span class="info-value">{{ formatDate(empInDate) }}</span>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import axios from 'axios';

const store = useStore();
const router = useRouter();

// 사원 정보
const pwaUser = computed(() => store.getters['pwa/pwaUser']);
const userName = computed(() => pwaUser.value?.display_name || pwaUser.value?.emp_name || '사원');
const userJob = computed(() => pwaUser.value?.emp_job || '-');
const sisulName = computed(() => pwaUser.value?.sisul_name || pwaUser.value?.m_sisul_name || '-');
const empInDate = computed(() => pwaUser.value?.emp_in_date || null);
const sisulCode = computed(() => pwaUser.value?.m_sisul_code || '');

// 최근 급여 정보
const recentWage = ref<any>(null);

// 공지사항
const latestNotice = ref<{ nc_idx: number; nc_title: string } | null>(null);
const noticeList = ref<any[]>([]);

// 공지사항 로드
const loadNotices = async () => {
  try {
    const response = await axios.get('/api/notice/list', {
      params: { page: 1, limit: 5 },
      withCredentials: true
    });
    
    if (response.data.success && response.data.notices.length > 0) {
      latestNotice.value = response.data.notices[0];
      noticeList.value = response.data.notices;
    }
  } catch (error) {
    console.error('공지사항 로드 오류:', error);
  }
};

// 공지사항 페이지로 이동
const goToNotice = () => {
  router.push('/emp/notice');
};

// 공지사항 상세로 이동
const goToNoticeDetail = (ncIdx: number) => {
  router.push(`/emp/notice/${ncIdx}`);
};

// 카테고리 텍스트
const getCategoryText = (category: string) => {
  const map: { [key: string]: string } = {
    'NOTICE': '공지',
    'UPDATE': '업뎃',
    'EVENT': '행사',
    'URGENT': '긴급'
  };
  return map[category] || '공지';
};

// 공지 날짜 포맷
const formatNoticeDate = (date: string) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getMonth() + 1}.${d.getDate()}`;
};

// 오늘 날짜
const todayText = computed(() => {
  const today = new Date();
  const options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric', 
    weekday: 'long' 
  };
  return today.toLocaleDateString('ko-KR', options);
});

// 빠른 메뉴
const quickMenus = [
  {
    path: '/emp/payslip',
    label: '급여명세',
    icon: 'mdi-file-document-outline',
    bgColor: '#dcfce7',
    iconColor: '#059669'
  },
  {
    path: '/emp/schedule',
    label: '일정 확인',
    icon: 'mdi-calendar-check',
    bgColor: '#dbeafe',
    iconColor: '#2563eb'
  },
  {
    path: '/emp/myinfo',
    label: '내 정보',
    icon: 'mdi-account-circle',
    bgColor: '#fef3c7',
    iconColor: '#d97706'
  },
  {
    path: '/emp/settings',
    label: '설정',
    icon: 'mdi-cog',
    bgColor: '#f3e8ff',
    iconColor: '#7c3aed'
  }
];

const navigateTo = (path: string) => {
  router.push(path);
};

// 날짜 포맷
const formatDate = (date: string | null | undefined): string => {
  if (!date) return '-';
  try {
    const d = new Date(date);
    return d.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return date;
  }
};

// 금액 포맷
const formatCurrency = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '0';
  const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
  if (isNaN(numValue)) return '0';
  return numValue.toLocaleString('ko-KR');
};

// 최근 급여 정보 조회
const fetchRecentWage = async () => {
  const empIdxVal = pwaUser.value?.emp_idx;
  if (!empIdxVal || !sisulCode.value) return;

  try {
    const year = new Date().getFullYear();
    const response = await axios.get(
      `/api/emp/payslip-history/${empIdxVal}/${sisulCode.value}/${year}`,
      { withCredentials: true }
    );

    if (response.data.success && response.data.data && response.data.data.length > 0) {
      const latest = response.data.data[0];
      recentWage.value = {
        ...latest,
        month: parseInt(latest.ewi_ym.substring(4, 6), 10),
        type: response.data.type // 'sabok' or 'yobo'
      };
    }
  } catch (error) {
    console.error('최근 급여 정보 조회 오류:', error);
  }
};

onMounted(() => {
  fetchRecentWage();
  loadNotices();
});
</script>

<style scoped lang="scss">
.emp-home-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.app-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 16px;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
  position: sticky;
  top: 0;
  z-index: 100;
  
  .header-title {
    font-size: 17px;
    font-weight: 600;
  }
}

.page-content {
  padding: 16px;
  padding-bottom: 80px;
}

.welcome-section {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  border-radius: 16px;
  padding: 20px;
  color: white;
  margin-bottom: 16px;
  
  .welcome-text {
    h2 {
      font-size: 20px;
      font-weight: 700;
      margin: 0 0 4px 0;
    }
    
    p {
      font-size: 14px;
      opacity: 0.9;
      margin: 0;
    }
  }
  
  .date-info {
    margin-top: 12px;
    
    .today {
      font-size: 13px;
      opacity: 0.85;
    }
  }
}

// 공지사항 배너
.notice-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  background: white;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  cursor: pointer;
  
  &:active {
    background: #f9f9f9;
  }
  
  .notice-badge {
    background: #059669;
    border-radius: 6px;
    padding: 4px 6px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .notice-text {
    flex: 1;
    font-size: 13px;
    font-weight: 500;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
}

.quick-menu-section {
  margin-bottom: 24px;
}

.quick-menu-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.quick-menu-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 8px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:active {
    transform: scale(0.95);
  }
  
  .menu-icon {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
  }
  
  .menu-label {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    text-align: center;
  }
}

// 최근 급여 섹션
.recent-wage-section {
  margin-bottom: 24px;
}

.wage-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:active {
    transform: scale(0.98);
  }
}

.wage-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  
  .wage-month {
    font-size: 15px;
    font-weight: 600;
    color: #374151;
  }
  
  .wage-status {
    padding: 4px 10px;
    font-size: 11px;
    font-weight: 600;
    background: #f3f4f6;
    color: #6b7280;
    border-radius: 20px;
    
    &.confirmed {
      background: #dcfce7;
      color: #059669;
    }
  }
}

.wage-amount {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 12px 0;
  border-bottom: 1px dashed #e5e7eb;
  margin-bottom: 12px;
  
  .label {
    font-size: 14px;
    color: #6b7280;
  }
  
  .amount {
    font-size: 26px;
    font-weight: 700;
    color: #059669;
    
    .unit {
      font-size: 14px;
      font-weight: 500;
      margin-left: 2px;
    }
  }
}

.wage-detail {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .detail-label {
    font-size: 13px;
    color: #9ca3af;
  }
  
  .detail-value {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    
    &.deduct {
      color: #dc2626;
    }
  }
}

.view-more {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  padding-top: 8px;
  border-top: 1px solid #f3f4f6;
  
  span {
    font-size: 13px;
    font-weight: 600;
    color: #059669;
  }
}

.notice-section {
  margin-bottom: 24px;
  
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    
    .more-link {
      font-size: 13px;
      color: #059669;
      font-weight: 600;
      cursor: pointer;
    }
  }
}

.notice-list-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.notice-list-item {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:active {
    background: #f9fafb;
  }
  
  .mini-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    margin-right: 8px;
    flex-shrink: 0;
    
    &.badge-notice { background: #e3f2fd; color: #1976d2; }
    &.badge-update { background: #e0f7fa; color: #0097a7; }
    &.badge-event { background: #e8f5e9; color: #388e3c; }
    &.badge-urgent { background: #ffebee; color: #d32f2f; }
  }
  
  .notice-title {
    flex: 1;
    font-size: 13px;
    color: #374151;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .notice-date {
    font-size: 11px;
    color: #9ca3af;
    margin-left: 8px;
    flex-shrink: 0;
  }
}

.empty-notice {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 20px;
  color: #9ca3af;
  
  span {
    margin-top: 8px;
    font-size: 13px;
  }
}

.info-summary-section {
  margin-bottom: 24px;
}

.info-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
  
  .info-label {
    font-size: 14px;
    color: #6b7280;
  }
  
  .info-value {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
  }
}

@media (max-width: 400px) {
  .quick-menu-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
